import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Modal,
  Stepper,
  Group,
  Button,
  Text,
  Stack,
  Box,
  Table,
  Badge,
  Select,
  Alert,
  Center,
  Loader,
  ThemeIcon,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import {
  ChevronLeft,
  ChevronRight,
  Upload as UploadIcon,
  FileText,
  Download,
  CheckCircle,
  AlertTriangle,
  Info,
} from 'react-feather';
import type { CohortWithDetails } from '../../../../types';
import {
  cohortCoachesApi,
  type PartnerCoachCSVColumnMapping,
  type PartnerCoachCSVRowResult,
  type PartnerCoachCSVTargetField,
  type PartnerCoachUploadPreviewResponse,
  type PartnerCoachUploadResult,
} from '../../../../services/api/cohortCoaches';
import { isMedleyEmail, MEDLEY_EMAIL_DOMAINS } from '../../../../utils/medleyEmail';

interface UploadPartnerCoachesModalProps {
  opened: boolean;
  onClose: () => void;
  cohort: CohortWithDetails;
  onUploadComplete: () => void;
}

type Step = 0 | 1 | 2 | 3;

interface TargetFieldOption {
  value: PartnerCoachCSVTargetField;
  label: string;
  required: boolean;
  /** MED-1355: required on each new coach row, but not to continue. */
  requiredForNewCoaches?: boolean;
  aliases: string[];
}

const TARGET_FIELDS: TargetFieldOption[] = [
  { value: 'first_name', label: 'First Name', required: true, aliases: ['firstname', 'first', 'fname', 'givenname'] },
  { value: 'last_name', label: 'Last Name', required: true, aliases: ['lastname', 'last', 'lname', 'surname', 'familyname'] },
  // MED-1355 QA round 5: Personal Email is required for each new coach. The
  // Medley Email (withmedley.com) is optional: the pool needs none. A row
  // matches an existing coach by either address.
  {
    value: 'preferred_email',
    label: 'Personal Email',
    required: false,
    requiredForNewCoaches: true,
    aliases: ['personalemailaddress', 'preferredemail', 'nonmedleyemail'],
  },
  {
    value: 'email',
    label: 'Medley Email',
    required: false,
    aliases: ['emailaddress', 'mail', 'medleyemailaddress', 'workemail'],
  },
  { value: 'phone', label: 'Phone', required: false, aliases: ['phonenumber', 'mobile', 'cell'] },
  { value: 'city', label: 'City', required: false, aliases: [] },
  { value: 'state_region', label: 'State/Region', required: false, aliases: ['state', 'region', 'province'] },
  { value: 'country', label: 'Country', required: false, aliases: [] },
  { value: 'timezone', label: 'Timezone', required: false, aliases: ['tz', 'timezonepreference'] },
];

const REQUIRED_TARGET_FIELDS: PartnerCoachCSVTargetField[] = ['first_name', 'last_name'];

/** The label in the Map To list, with the same required mark as the help text. */
function targetFieldLabel(field: TargetFieldOption): string {
  if (field.required) return `${field.label} *`;
  if (field.requiredForNewCoaches) return `${field.label} (new coaches) *`;
  return field.label;
}

// A Badge keeps its full label. Without this, a narrow table cell shrinks the
// badge and its label ends in an ellipsis ("ER...").
const FULL_BADGE = { root: { minWidth: 'max-content' } };

function normalize(text: string): string {
  return text.toLowerCase().replace(/[\s_\-/]/g, '');
}

/** The non-empty, trimmed values in one CSV column. */
function columnValues(data: string[][], colIndex: number): string[] {
  const values: string[] = [];
  for (const row of data) {
    const v = row[colIndex]?.trim();
    if (v) values.push(v);
  }
  return values;
}

/** The target whose name or alias matches the header, or null. */
function matchHeader(header: string): PartnerCoachCSVTargetField | null {
  const norm = normalize(header);
  const field = TARGET_FIELDS.find(f =>
    [f.value, f.label, ...f.aliases].map(normalize).includes(norm),
  );
  return field?.value ?? null;
}

/**
 * MED-1355: headers that name one email field. A header such as "Medley
 * Email" maps by its name, the same as every other column, whatever its
 * values are. The review step then shows each bad value as a row error.
 */
const NAMED_EMAIL_HEADERS = new Set(
  ['Medley Email', 'Medley Email Address', 'Personal Email', 'Personal Email Address', 'Preferred Email', 'Non-Medley Email'].map(normalize),
);

/**
 * MED-1355: map each CSV header to a target field. A header that names one
 * email field (for example "Medley Email") maps by name. A general email
 * header (for example "Email") maps by its values: to Medley Email only when
 * every value is at a Medley domain, else to Personal Email. A column with
 * no values keeps the match by name. A header whose name and values agree
 * claims its field first.
 */
function autoMapHeaders(headers: string[], data: string[][]): PartnerCoachCSVColumnMapping[] {
  const wanted = headers.map((header, colIndex) => {
    const byName = matchHeader(header);
    if (byName !== 'email' && byName !== 'preferred_email') {
      return { field: byName, byName: true };
    }
    if (NAMED_EMAIL_HEADERS.has(normalize(header))) return { field: byName, byName: true };
    const values = columnValues(data, colIndex);
    if (values.length === 0) return { field: byName, byName: true };
    const byValues: PartnerCoachCSVTargetField = values.every(v => isMedleyEmail(v))
      ? 'email'
      : 'preferred_email';
    return { field: byValues, byName: byValues === byName };
  });
  const used = new Set<PartnerCoachCSVTargetField>();
  const result: PartnerCoachCSVColumnMapping[] = headers.map(header => ({
    csvColumn: header,
    targetField: null,
  }));
  for (const pass of [true, false]) {
    wanted.forEach(({ field, byName }, i) => {
      if (!field || byName !== pass || used.has(field)) return;
      used.add(field);
      result[i] = { csvColumn: headers[i], targetField: field };
    });
  }
  return result;
}

/** The API row error for a Medley Email value that is not at a Medley domain. */
const API_NOT_MEDLEY_EMAIL_ERROR = `Email must be a Medley email (an address at ${MEDLEY_EMAIL_DOMAINS.join(' or ')}). Put personal addresses in the Personal Email column.`;

/**
 * MED-1355: the API sends row errors as text with no code. Replace the
 * Medley domain error with a fix the CM can do here. When no value in the
 * Medley Email column is a Medley address and no column is mapped to
 * Personal Email, the column is the wrong one. Otherwise the fix is the
 * value. The Medley Email is optional, so a blank value is always a fix.
 */
function rowErrorText(error: string, suggestPersonalEmailColumn: boolean): string {
  return error
    .split('; ')
    .map(part => {
      if (part !== API_NOT_MEDLEY_EMAIL_ERROR) return part;
      return suggestPersonalEmailColumn
        ? 'This is not a withmedley.com address. Go Back and map this column to Personal Email.'
        : "This is not a withmedley.com address. Put the coach's withmedley.com address in the Medley Email column, or leave it blank.";
    })
    .join('; ');
}

function parseCSV(text: string): { headers: string[]; data: string[][] } {
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
  if (lines.length < 2) {
    throw new Error('CSV must have a header row and at least one data row');
  }
  const parseRow = (row: string): string[] => {
    const out: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < row.length; i++) {
      const ch = row[i];
      if (ch === '"') {
        if (inQuotes && row[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        out.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    out.push(current.trim());
    return out;
  };
  return {
    headers: parseRow(lines[0]),
    data: lines.slice(1).map(parseRow),
  };
}

function rowStatusBadge(status: PartnerCoachCSVRowResult['status']) {
  switch (status) {
    case 'new_partner_coach':
      return <Badge color="medleyGreen" variant="light" styles={FULL_BADGE}>New</Badge>;
    case 'existing_partner_coach':
      return <Badge color="blue" variant="light" styles={FULL_BADGE}>Existing partner</Badge>;
    case 'existing_medley_coach_dual':
      return <Badge color="grape" variant="light" styles={FULL_BADGE}>Existing Medley coach</Badge>;
    case 'already_potential':
      return <Badge color="gray" variant="light" styles={FULL_BADGE}>Already potential</Badge>;
    case 'error':
      return <Badge color="dangerRed" variant="light" styles={FULL_BADGE}>Error</Badge>;
    default:
      return null;
  }
}

export function UploadPartnerCoachesModal({
  opened,
  onClose,
  cohort,
  onUploadComplete,
}: UploadPartnerCoachesModalProps) {
  const [activeStep, setActiveStep] = useState<Step>(0);
  const [file, setFile] = useState<File | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [mappings, setMappings] = useState<PartnerCoachCSVColumnMapping[]>([]);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<PartnerCoachUploadPreviewResponse | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<PartnerCoachUploadResult | null>(null);

  const reset = useCallback(() => {
    setActiveStep(0);
    setFile(null);
    setCsvHeaders([]);
    setCsvData([]);
    setMappings([]);
    setPreview(null);
    setResult(null);
  }, []);

  useEffect(() => {
    if (opened) reset();
  }, [opened, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileDrop = async (files: File[]) => {
    const selected = files[0];
    if (!selected) return;
    try {
      const text = await selected.text();
      const { headers, data } = parseCSV(text);
      setFile(selected);
      setCsvHeaders(headers);
      setCsvData(data);
      setMappings(autoMapHeaders(headers, data));
      setActiveStep(1);
    } catch (err) {
      notifications.show({
        title: 'CSV Error',
        message: err instanceof Error ? err.message : 'Failed to parse CSV file',
        color: 'red',
      });
    }
  };

  const handleDownloadTemplate = async () => {
    const headers = ['First Name', 'Last Name', 'Personal Email', 'Medley Email', 'Phone', 'City', 'State/Region', 'Country', 'Timezone'];
    const csv = headers.join(',') + '\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'partner-coaches-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const updateMapping = (csvColumn: string, targetField: PartnerCoachCSVTargetField | null) => {
    setMappings(prev =>
      prev.map(m => {
        if (m.csvColumn === csvColumn) {
          return { ...m, targetField };
        }
        // enforce uniqueness: clear other mappings that claimed the same target
        if (targetField && m.targetField === targetField) {
          return { ...m, targetField: null };
        }
        return m;
      }),
    );
  };

  const mappedTargets = useMemo(
    () => new Set(mappings.map(m => m.targetField).filter(Boolean) as PartnerCoachCSVTargetField[]),
    [mappings],
  );
  const missingRequired = REQUIRED_TARGET_FIELDS.filter(f => !mappedTargets.has(f));
  // MED-1355 QA round 5: a row needs an address to match or create a coach.
  // With no email column mapped at all, the preview cannot run. With only a
  // Medley Email column, rows for existing coaches still work, so a missing
  // Personal Email column warns but does not block.
  const noEmailColumn = !mappedTargets.has('preferred_email') && !mappedTargets.has('email');
  const personalEmailUnmapped = !mappedTargets.has('preferred_email');

  const canGoToPreview = activeStep === 1 && missingRequired.length === 0 && !noEmailColumn;

  /** Values in a mapped column that are not Medley addresses. */
  const nonMedleyCount = (csvColumn: string): { nonMedley: number; total: number } => {
    const values = columnValues(csvData, csvHeaders.indexOf(csvColumn));
    return { nonMedley: values.filter(v => !isMedleyEmail(v)).length, total: values.length };
  };
  const medleyColumn = mappings.find(m => m.targetField === 'email')?.csvColumn;
  const medleyColumnCounts = medleyColumn ? nonMedleyCount(medleyColumn) : null;
  const medleyColumnHasNoMedleyEmail =
    !!medleyColumnCounts &&
    medleyColumnCounts.total > 0 &&
    medleyColumnCounts.nonMedley === medleyColumnCounts.total;
  // MED-1355: tell the CM to map the Medley Email column to Personal Email
  // only when that is possible: no other column is mapped to Personal Email.
  const suggestPersonalEmailColumn = medleyColumnHasNoMedleyEmail && personalEmailUnmapped;
  const missingNameLabels = missingRequired.map(f => TARGET_FIELDS.find(t => t.value === f)?.label);

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const response = await cohortCoachesApi.uploadPreview(cohort.id, {
        mappings,
        csvData,
      });
      setPreview(response);
      setActiveStep(2);
    } catch (err) {
      notifications.show({
        title: 'Preview Error',
        message: err instanceof Error ? err.message : 'Failed to generate preview',
        color: 'red',
      });
    } finally {
      setPreviewing(false);
    }
  };

  const runUpload = async () => {
    setUploading(true);
    try {
      const response = await cohortCoachesApi.uploadExecute(cohort.id, {
        mappings,
        csvData,
      });
      setResult(response);
      setActiveStep(3);
      notifications.show({
        title: 'Upload Complete',
        message: `${response.addedToPotential} coach${response.addedToPotential !== 1 ? 'es' : ''} added to potential list`,
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Upload Error',
        message: err instanceof Error ? err.message : 'Failed to upload coaches',
        color: 'red',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleFinish = () => {
    onUploadComplete();
    handleClose();
  };

  const handleBack = () => {
    if (activeStep === 0) return;
    if (activeStep === 2) setPreview(null);
    setActiveStep((prev) => (prev - 1) as Step);
  };

  const sampleValues = (csvColumn: string): string => {
    const colIndex = csvHeaders.indexOf(csvColumn);
    if (colIndex < 0) return '';
    const unique = new Set<string>();
    for (const row of csvData) {
      const v = row[colIndex]?.trim();
      if (v) unique.add(v);
      if (unique.size >= 3) break;
    }
    return Array.from(unique).join(', ');
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Stack gap={4}>
          <Text fw={600} size="lg">Upload Partner Coaches</Text>
          <Text size="sm" c="dimmed">
            {cohort.name} • {cohort.partner_name ?? 'Partner'}
          </Text>
        </Stack>
      }
      size="xl"
      styles={{
        body: { padding: 0 },
        header: { padding: '20px 24px', borderBottom: '1px solid var(--mantine-color-gray-2)' },
      }}
    >
      <Stack gap={0}>
        <Box px="md" pt="md">
          <Stepper active={activeStep} size="xs" color="medleyGreen">
            <Stepper.Step label="Upload" />
            <Stepper.Step label="Map Columns" />
            <Stepper.Step label="Review" />
            <Stepper.Step label="Complete" />
          </Stepper>
        </Box>

        <Box p="md">
          {activeStep === 0 && (
            <Stack gap="md">
              <Dropzone
                onDrop={handleFileDrop}
                accept={{ 'text/csv': ['.csv'] }}
                maxFiles={1}
                py="xl"
              >
                {file ? (
                  <Stack align="center" gap="xs">
                    <ThemeIcon size={60} radius={60} variant="light" color="medleyGreen">
                      <FileText size={30} />
                    </ThemeIcon>
                    <Text fw={500}>{file.name}</Text>
                    <Text size="sm" c="dimmed">{csvData.length} rows detected</Text>
                  </Stack>
                ) : (
                  <Stack align="center" gap="xs">
                    <UploadIcon size={32} color="gray" />
                    <Text fw={500}>Drag and drop your CSV file here</Text>
                    <Text size="sm" c="dimmed">or click to browse</Text>
                  </Stack>
                )}
              </Dropzone>
              <Group justify="center">
                <Button
                  variant="subtle"
                  leftSection={<Download size={16} />}
                  onClick={handleDownloadTemplate}
                >
                  Download template CSV
                </Button>
              </Group>
            </Stack>
          )}

          {activeStep === 1 && (
            <Stack gap="md">
              {missingNameLabels.length > 0 && (
                <Alert color="warningAmber" variant="light" icon={<AlertTriangle size={16} />}>
                  Map a column to {missingNameLabels.join(' and ')}.
                </Alert>
              )}
              {noEmailColumn ? (
                <Alert color="warningAmber" variant="light" icon={<AlertTriangle size={16} />}>
                  Map a Personal Email column. Each new coach needs a personal email. A Medley
                  Email column is optional.
                </Alert>
              ) : (
                personalEmailUnmapped && (
                  <Alert color="warningAmber" variant="light" icon={<AlertTriangle size={16} />}>
                    Map a Personal Email column. Each new coach needs a personal email, or the
                    row shows an error.
                  </Alert>
                )
              )}
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>CSV Column</Table.Th>
                    <Table.Th>Sample Values</Table.Th>
                    <Table.Th>Map To</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {mappings.map(m => (
                    <Table.Tr key={m.csvColumn}>
                      <Table.Td>
                        <Text size="sm" fw={500}>{m.csvColumn}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed" truncate>{sampleValues(m.csvColumn) || '—'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Select
                          placeholder="Ignore column"
                          clearable
                          value={m.targetField}
                          onChange={(val) =>
                            updateMapping(m.csvColumn, (val as PartnerCoachCSVTargetField) || null)
                          }
                          data={TARGET_FIELDS.map(f => ({
                            value: f.value,
                            label: targetFieldLabel(f),
                            disabled: mappedTargets.has(f.value) && m.targetField !== f.value,
                          }))}
                          w={260}
                          size="xs"
                          aria-label={`Map ${m.csvColumn} to`}
                        />
                        {/* MED-1355: warn when a Medley Email column holds
                            other addresses. Every such row fails. */}
                        {m.targetField === 'email' && (() => {
                          const { nonMedley, total } = nonMedleyCount(m.csvColumn);
                          if (nonMedley === 0) return null;
                          return (
                            <Text size="xs" fw={500} c="var(--medley-text-warning)" mt={4} maw={260}>
                              {nonMedley < total
                                ? 'Some of these are not withmedley.com addresses. Those rows will show an error. Leave a cell blank if the coach has no Medley email.'
                                : personalEmailUnmapped
                                  ? 'These are not withmedley.com addresses. Map this column to Personal Email.'
                                  : 'These are not withmedley.com addresses. Each row will show an error. Correct the values in the file, or ignore this column.'}
                            </Text>
                          );
                        })()}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              <Text size="xs" c="dimmed">
                <Info size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                Unmapped columns will be ignored. First Name and Last Name are required. Personal
                Email is required for each new coach and cannot be a withmedley.com address.
                Medley Email is optional. When given, it must be an address at withmedley.com. A
                row matches an existing coach by either email.
              </Text>
            </Stack>
          )}

          {activeStep === 2 && (
            <Stack gap="md">
              {previewing || !preview ? (
                <Center py="xl"><Loader /></Center>
              ) : (
                <>
                  <Group gap="lg">
                    <Text size="sm">
                      <b>{preview.newPartnerCoaches}</b> new
                    </Text>
                    <Text size="sm">
                      <b>{preview.existingPartnerCoaches}</b> existing partner
                    </Text>
                    <Text size="sm">
                      <b>{preview.existingMedleyCoachesDual}</b> existing Medley
                    </Text>
                    <Text size="sm">
                      <b>{preview.alreadyPotential}</b> already potential
                    </Text>
                    <Text size="sm" c="dangerRed">
                      <b>{preview.errorRows}</b> {preview.errorRows === 1 ? 'error' : 'errors'}
                    </Text>
                  </Group>
                  <Box style={{ maxHeight: 420, overflowY: 'auto' }}>
                    <Table striped>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Row</Table.Th>
                          <Table.Th>Name</Table.Th>
                          <Table.Th>Personal Email</Table.Th>
                          <Table.Th>Medley Email</Table.Th>
                          <Table.Th>Status</Table.Th>
                          <Table.Th miw={200}>Note</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {preview.rows.map(row => (
                          <Table.Tr key={row.rowIndex}>
                            <Table.Td>{row.rowIndex}</Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {[row.data.first_name, row.data.last_name].filter(Boolean).join(' ') || '—'}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{row.data.preferred_email || 'Not provided'}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{row.data.email || 'Not provided'}</Text>
                            </Table.Td>
                            <Table.Td>{rowStatusBadge(row.status)}</Table.Td>
                            <Table.Td>
                              {/* The whole note shows, wrapped, with no hover needed. */}
                              {row.errors.length > 0 ? (
                                <Text size="xs" c="dangerRed" style={{ overflowWrap: 'anywhere' }}>
                                  {rowErrorText(row.errors.join('; '), suggestPersonalEmailColumn)}
                                </Text>
                              ) : row.notes.length > 0 ? (
                                <Text size="xs" c="dimmed" style={{ overflowWrap: 'anywhere' }}>
                                  {row.notes.join('; ')}
                                </Text>
                              ) : (
                                <Text size="xs" c="dimmed">—</Text>
                              )}
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Box>
                </>
              )}
            </Stack>
          )}

          {activeStep === 3 && result && (
            <Stack align="center" gap="md" py="xl">
              <ThemeIcon size={64} radius={64} color="medleyGreen" variant="light">
                <CheckCircle size={32} />
              </ThemeIcon>
              <Text fw={600} size="lg">Upload Complete</Text>
              <Text size="sm" c="dimmed" ta="center">
                {result.created} partner coach{result.created !== 1 ? 'es' : ''} created,{' '}
                {result.matched} existing coach{result.matched !== 1 ? 'es' : ''} added,{' '}
                {result.skipped} row{result.skipped !== 1 ? 's' : ''} skipped.
              </Text>
              {result.errors.length > 0 && (
                <Alert color="dangerRed" variant="light" w="100%">
                  <Text size="sm" fw={500}>{result.errors.length} error(s):</Text>
                  <Stack gap={2} mt="xs">
                    {result.errors.slice(0, 5).map(e => (
                      <Text key={e.row} size="xs">
                        Row {e.row}: {rowErrorText(e.error, suggestPersonalEmailColumn)}
                      </Text>
                    ))}
                    {result.errors.length > 5 && (
                      <Text size="xs" c="dimmed">
                        +{result.errors.length - 5} more errors
                      </Text>
                    )}
                  </Stack>
                </Alert>
              )}
            </Stack>
          )}
        </Box>

        <Box p="md" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
          <Group justify="space-between">
            <Button variant="subtle" onClick={handleClose}>
              Cancel
            </Button>
            <Group gap="xs">
              {activeStep > 0 && activeStep < 3 && (
                <Button
                  variant="outline"
                  leftSection={<ChevronLeft size={16} />}
                  onClick={handleBack}
                  disabled={previewing || uploading}
                >
                  Back
                </Button>
              )}
              {activeStep === 1 && (
                <Button
                  color="medleyGreen"
                  rightSection={<ChevronRight size={16} />}
                  disabled={!canGoToPreview}
                  loading={previewing}
                  onClick={runPreview}
                >
                  Preview
                </Button>
              )}
              {activeStep === 2 && preview && (
                <Button
                  color="medleyGreen"
                  loading={uploading}
                  disabled={
                    preview.newPartnerCoaches +
                      preview.existingPartnerCoaches +
                      preview.existingMedleyCoachesDual ===
                    0
                  }
                  onClick={runUpload}
                >
                  Confirm Upload
                </Button>
              )}
              {activeStep === 3 && (
                <Button color="medleyGreen" onClick={handleFinish}>
                  Done
                </Button>
              )}
            </Group>
          </Group>
        </Box>
      </Stack>
    </Modal>
  );
}

export default UploadPartnerCoachesModal;
